import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { vapiOutboundAPI } from '../../../api/client';
import { PhoneArrowUpRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export default function VoiceEmailSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calling, setCalling] = useState(false);

  const [mdPhone, setMdPhone] = useState('');
  const [vapiNumberId, setVapiNumberId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vapiOutboundAPI.getSettings();
      setMdPhone(res.data.md_phone_number || '');
      setVapiNumberId(res.data.vapi_phone_number_id || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Voice Email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await vapiOutboundAPI.updateSettings({
        md_phone_number: mdPhone,
        vapi_phone_number_id: vapiNumberId,
      });
      setMdPhone(res.data.md_phone_number || '');
      setVapiNumberId(res.data.vapi_phone_number_id || '');
      toast.success('Voice Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const callMeNow = async () => {
    setCalling(true);
    try {
      const res = await vapiOutboundAPI.callMd();
      toast.success(res.data.message || 'Calling you now…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not place the call');
    } finally {
      setCalling(false);
    }
  };

  const callReady = !!mdPhone && !!vapiNumberId;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <p className="font-semibold text-slate-800">Settings</p>
          <p className="text-sm text-slate-500">Where Vapi should call, and which number to call from</p>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">MD's Phone Number</label>
            <input
              type="tel"
              value={mdPhone}
              onChange={(e) => setMdPhone(e.target.value)}
              placeholder="+94771234567"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              The number Vapi will call. Use international format, e.g. <code>+94771234567</code>.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vapi Phone Number ID</label>
            <input
              type="text"
              value={vapiNumberId}
              onChange={(e) => setVapiNumberId(e.target.value)}
              placeholder="e.g. 8b1e2f3a-..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              The "from" number's ID from your Vapi dashboard (Phone Numbers). Not a phone number —
              it's the ID Vapi assigns to a registered number.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Call Me Now card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 flex items-center gap-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 flex-shrink-0">
            <PhoneArrowUpRightIcon className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">Call Me Now</p>
            <p className="text-sm text-slate-500">Vapi calls you and connects you to the Email Assistant</p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Say "read my emails" once connected, and the assistant will read your unread inbox aloud and
            can draft replies from what you say.
          </p>

          <button
            onClick={callMeNow}
            disabled={calling || !callReady}
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <PhoneArrowUpRightIcon className="w-4 h-4" />
            {calling ? 'Calling…' : 'Call Me Now'}
          </button>

          {!callReady && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <InformationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Set up required before you can call:</p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-700">
                  {!mdPhone && <li>• Set MD's phone number above</li>}
                  {!vapiNumberId && <li>• Set the Vapi phone number ID above</li>}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
